import {
  Component,
  Element,
  Event,
  type EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import type {
  Facet, FacetValue,
  Filter,
  SearchApi,
  SearchApiDocument
} from '../../../api/SearchApi';
import {type FacetChange} from '../facets/FacetChange';
import {isMobile} from '../../../utils/media';

import {
  type ReflectedTab,
  type SearchCancelledEvent,
  type SearchCompletedEvent,
  type SearchErrorEvent,
  type SearchAutocorrectEvent,
  type SearchStartedEvent,
  type MiddlewaresCompleted
} from '../../SearchCompletedEvent';
import type {ResultRenderer} from '../../../utils/Renderer';
import {type FacetChangedEvent, type FilterAction} from './FacetChangedEvent';
import {type SearchResultClickEvent} from './SearchResultClickEvent';
import {type ViewItemListEvent} from './ViewItemListEvent';
import {ITEMS_PER_PAGE} from '../../../constants/paginationConstants';
import {viewType} from '../../../constants/viewTypeConstants';
import {type ViewChangeEvent} from './ViewChangeEvent';
import {type SearchResultsPresentationEvent} from './SearchResultsPresentationEvent';
import {
  type MiddlewarePipeAdapterConfig,
  middlewareSearchTabAdapater
} from '../../../utils/middleware-pipe/middleware-search-tab.adapater';
import {type PipeMiddlewareElement, vendorMiddlewares} from '../../../utils/middleware-pipe/middleware-pipe';
import type {PipesRegistry} from '../../../utils/middleware-pipe/models/stack-pipes-registry.model';
import type {
  PipeDetailsAdditionalData,
} from '../../../utils/middleware-pipe/models/middleware-pipe-details.model';
import type {SearchTableConfiguration, SortableHeader} from './tabular/SearchTableConfiguration';
import {first, firstValueFrom, Observable, Subject, take, takeUntil, tap} from 'rxjs';
import {FacetsService, type FilterRequestUnique} from '../../../utils/services/facets/facets.service';
import type {FacetResponse} from '../../../models/cose-api/response/facet-options.model';
import {type SearchableFacetInputChanged} from '../facets/subcomponents/SearchableFacetInputChanged';
import {SearchableInputFilter} from '../facets/subcomponents/searchable-input-filter-model';
import {FacetTypeEnum, type DateFacet, type NestedFilter} from '../../../api/model/response/facets/Facets';
import type {SearchableFilterChanged} from '../facets/subcomponents/searchable-filter-changed';
import {Strategies, StrategiesFactory} from '../../strategies/Strategies';
import {makeLogger} from '../../../logger/logger';
import {TabularView} from './subcomponents/tabular-view/tabular-view';
import {DefaultView} from './subcomponents/default-view/default-view';
import {Filters} from './subcomponents/filters/filters';
import {FiltersConditionalWraper} from './subcomponents/filters/filters-conditional-wrapper';
import {ResultsSorting} from './subcomponents/result-sorting/results-sorting';
import {sortingOptions} from '../../../constants/sortingConstants';
import {OnlyYourCountryApc} from './subcomponents/only-your-country-apc/only-your-country-apc';
import {GridSwitchButton} from './subcomponents/grid-switch-button/grid-switch-button';
import {TabTitle} from './subcomponents/tab-title/tab-title';
import {FacetsPane} from './subcomponents/facets-pane/facets-pane';
import {ResultList} from './subcomponents/result-list/result-list';
import {NoResults} from './subcomponents/no-results/no-results';
import {NumberOfResults} from './subcomponents/number-of-results/number-of-results';
import {ExstResponseTranslator} from '../../../utils/libs/exst-translate/exst-response-translator';
import {ProductCatalogChip} from './subcomponents/product-catalog/product-catalog-chip';
import {type BaseUrlsStrategyService} from '../../strategies/BasePathStrategyService';
import type {BaseUrlsStrategyData} from '../../strategies/types/base-urls-data.type';
import {
  BASE_URLS,
  CONVERSATIONAL_SEARCH,
  HIDE_LABELS
} from '../../strategies/utils/variables';
import ProductSidePanelService from '../product-side-panel/product-side-panel.service';
import {type HideLabelsStrategyService} from '../../strategies/HideLabelsStrategyService';
import {makeTabName} from './util';

import {EolDiscontinuedResultList} from './subcomponents/eol-discontinued-result-list/eol-discontinued-result-list';
import type {IEolDiscontinuedDetails} from './subcomponents/types/subcomponents-types.type';
import {CoseRequestService, QueryContext} from '../../../utils/services/cose/cose-request.service';
import {FacetsV2Service} from '../../../utils/services/facets/facets-v2.service';
import type {
  FacetsConfigStrategyDataParams
} from '../../strategies/types/facets-config-strategy-data.type';
import {cloneObjectWithNoReference} from '../../../utils/helpers/clone-object-with-no-reference';
import type {FilterNode} from '../facets-build-wrapper/filter-node';
import {getSiteSearchCategory} from '../../analytics-reporting/util';


import type {SearchTabDataFlux} from './dataflux';
import {findParentValueInNestedArrayObject} from '../../../utils/helpers/find-parent-value-in-nested-array-object';
import type {labelProviderInterface} from '../../LabelProviderInterface';
import DataFlux from '../../../utils/libs/dataflux/dataflux';
import EventBus from '../../../utils/libs/eventbus/eventbus';
import type {UiaasSearchDataFlux} from '../search/dataflux';
import {Tab} from '../../../constants/searchers';
import {FilterChangeContext} from '../facets-build-wrapper/models/filter-change-context.enum';

const {info, debug, warn, err} = makeLogger({pathToCurrentFile: 'components/result-page/search-tab/search-tab.tsx'});

@Component({
  tag: 'uiaas-search-tab',
  shadow: true,
  styleUrl: 'search-tab.scss'
})
export class SearchTab {
  get baseUrls(): BaseUrlsStrategyData {
      return this._baseUrls;
  }

  set baseUrls(value: BaseUrlsStrategyData) {
      this._baseUrls = value;
  }
  get strategies(): Strategies {
      return this._strategies;
  }

  set strategies(value: Strategies) {
      this._strategies = value;
  }
  get facetsV2Service(): FacetsV2Service {
      return this._facetsV2Service;
  }

  set facetsV2Service(value: FacetsV2Service) {
      this._facetsV2Service = value;
  }
  get facetService(): FacetsService {
      return this._facetService;
  }

  set facetService(value: FacetsService) {
      this._facetService = value;
  }

  get coseRequestService(): CoseRequestService {
      return this._coseRequestService;
  }

  set coseRequestService(value: CoseRequestService) {
      this._coseRequestService = value;
  }

  private _facetService!: FacetsService;
  private _facetsV2Service!: FacetsV2Service;
  private _coseRequestService!: CoseRequestService;
  private _strategies: Strategies;
  private _baseUrls:BaseUrlsStrategyData;
  private facets$: Observable<FacetResponse[]>;
  private facetsConfigDataParams: FacetsConfigStrategyDataParams | null;
  private sortOption;

  get facetsConfigDataParamsGetter(): FacetsConfigStrategyDataParams | null {
      return this.facetsConfigDataParams;
  }

  get currentPage(): number {
      return this._currentPage;
  }

  set currentPage(value: number) {
      this._currentPage = value;
  }

  get resultsCurrentPage(): number {
      return this.queryResult?.pagination && !!this.queryResult?.pagination?.pageCurrent ? this.queryResult.pagination.pageCurrent : 1;
  }

  constructor() {
      this.resultClickAnalytics = this.resultClickAnalytics.bind(this);
  }


  private unsubscriber$: Subject<any> = new Subject<any>(); // eslint-disable-line

  private getFacetsConfigStrategyData(): FacetsConfigStrategyDataParams | null { // eslint-disable-line
      if(this.strategies?.strategyData && this.strategies.strategyData?.facetsConfig) {
        return this.strategies.strategyData.facetsConfig.params;
      }

      return null;
  }

    @Method()
  async tabId(): Promise<string> {
      return this.idProp;
  }

    @Element() el;

    @State() queryResult: SearchApi | any; // eslint-disable-line
    @State() isCountryOnly = true;
    @State() additionalCountries: Array<string> = [];
    @State() alternativeSearcherActive = false;

    private handleChange(checked: boolean) {
        this.isCountryOnly = checked;
        if (this.isCountryOnly) {
          this.additionalCountries = [];
          this.alternativeSearcherActive = false;
        } else {
          this.alternativeSearcherActive = true;
          this.additionalCountries = ['all'];
        }

        this.checkExistAndQueryCall();
    }

    @Event({
      eventName: 'search:FacetClickEvent',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      facetChangedEventEmitter: EventEmitter<FacetChangedEvent>;
    @Event({
      eventName: 'search:SearchResultClickEvent',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      searchResultClickEmitter: EventEmitter<SearchResultClickEvent>;
    @Event({
      eventName: 'search:ViewItemListEvent',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      ViewItemListEventEmitter: EventEmitter<ViewItemListEvent>;
    @Event({
      eventName: 'search:viewTypeChangeClickEvent',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      viewChangeEmitter: EventEmitter<ViewChangeEvent>;
    @Event({
      eventName: 'search:SearchResultsPresentationEvent',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      SearchResultsPresentationEventEmitter: EventEmitter<SearchResultsPresentationEvent>;
    @Event({
      eventName: 'searchMount',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      SearchMountEmitter: EventEmitter<string>;
    /**
     * Send the chip value to the parent component when clicking the close button of a chip.
     */
    @Event({
      eventName: 'facest:chipRemoveOnTab',
      composed: true,
      cancelable: true,
      bubbles: true
    }) chipRemoveOnTab: EventEmitter<unknown>;
    /**
     * All tabs elements data to opulate dashboard view
     */
    @Prop() readonly reflectedTabs: Object;

  /**
   * Is eCommerce mode is on=true or off=false.
   */
  @Prop() readonly isEcommerceMode?: boolean;

  /**
   * Is Additional Conditions Widget is on=true or off=false.
   */
  @Prop() readonly isAdditionalConditionsWidgetActive?: boolean;

  /**
   * Is Made In America Promotion Widget is on=true or off=false.
   */
  @Prop() readonly isMiaPromotionWidget?: boolean;

  /**
   * Is Product N/A Country Selector enabled
   */
  @Prop() readonly isProductNaCountrySelector?: boolean = false;

  /**
   * Is Unified Checkout integration enabled
   */
  @Prop() readonly isUnifiedCheckout?: boolean = false;

  /**
   * Check if invalid references are present in the distributor stock
   */
  @Prop() readonly checkDistributorStockInvalidIds?: boolean = false;

  /**
   * Component Id
   */
  @Prop({attribute: 'id'}) readonly idProp: string =
    'here-create-random-string';
  /**
   * Application name. Passed to API. Passed from Search Component, but you can override here.
   */
  @Prop() readonly appName: string;
  /**
   * Country code. Passed to API. Passed from Search Component, but you can override here.
   */
  @Prop() readonly country: string;
  /**
   * Language name. Passed to API. Passed from Search Component, but you can override here.
   */
  @Prop() readonly language: string;
  /**
   * Query to execute the search for
   */
  @State() query = '';
  /**
   * Label Provider. Passed from Search Component, however you can override it here.
   * For documentation, please refer to the Search Component.
   */
  @Prop({
    mutable: true
  })
    labelProvider?: labelProviderInterface;
  /**
   * How long to wait for results from tab's API before give up. Milliseconds.
   */
  @Prop() readonly timeout?: number;
  /**
   * Show the tabs title above the result list?
   */
  @Prop() readonly showTitle: boolean = false;
  /**
   * Show the document count near the title? Has no effect when showTitle=false
   */
  @Prop() readonly showDocumentCount: boolean = true;

  /**
   * Show the number of displayed results and term for selected tab (above search result list)
   */
  @Prop() readonly resultSummary: boolean = false;
  /**
   * Internal name, prefix for labels names
   */
  @Prop({mutable: true}) tabName?: string;
  /**
   * Searcher parameter in query for alternative search
   */
  @Prop() readonly alternativeSearcher?: string;
  /**
   * Shown in the widget as title of the Federated Tab
   */
  @Prop() readonly tabTitle?: string;

  /**
   * Enables right to left view mode
   */
  @Prop() readonly rtl: boolean;

  /**
   *  Enables scoring for SRT app
   */
  @Prop() readonly srt: boolean = false;

  /**
   *  Enables dahsboard view for tab
   */
  @Prop() readonly isDashboardView: boolean = false;

  /**
   * Shown in the widget as icon of the Federated Tab
   */
  @Prop() readonly tabIcon?: string;
  @State() apiEndpoint: URL; // https://stackoverflow.com/questions/38197096/typescript-what-is-type-url
  /**
   * Where to call for the data? Must follow Search API specs from the SE Search Team.
   */
  @Prop({
    attribute: 'api-endpoint'
  })
  readonly apiEndpointProp: URL | string;
  /**
   * When false, tab is not visible to the user
   */

  /**
   * The Apigee Bearer
   */
  @Prop({attribute: 'apigee-bearer'}) readonly apigeeBearer: string;
  /**
   * The Apigee Auth
   */
  @Prop({attribute: 'apigee-auth'}) readonly apigeeAuth: string;
  /**
   * The Apigee Token URL
   */
  @Prop({attribute: 'apigee-token-url'}) readonly apigeeTokenUrl: string;
  /**
   * The API Key
   */
  @Prop({attribute: 'api-key'}) readonly apiKey: string;
  /**
   * The IDMS User token
   */
  @Prop({attribute: 'token'}) readonly token: string;
  /**
   * The APIGee Token get
   */
  @Prop({attribute: 'with-api-gee-token'}) readonly withApiGeeToken: boolean = false;

  /**
   * Shows chips under result column (chips shows only filter names, not group by facets)
   */
  @Prop({mutable: false, attribute: 'show-simple-chips'}) readonly showSimpleChips: boolean = false;
  /**
   * Hide facets with id in prop array nad SimpleChips belongs to this facets
   */
  @Prop({mutable: false, attribute: 'hidden-facets'}) readonly hiddenFacets: string[] = [];


  @State() isVisible: boolean = false;

  /**
   * The IDMS Federated ID of the user. If you need to search for users private data, provide the Federated ID here
   */
  @Prop({attribute: 'idms-federated-id'}) readonly federatedId: string;

  /**
   * Used by pipe middleware to list all applied middlewares
   */
  @Prop() readonly middlewares: string;

  /**
   * Enables result sorting selector
   */
  @Prop() readonly sortingResults: boolean = false;

  @Prop() readonly scrollFacetsToTop: boolean = true;

  /**
   * Enables smart country selector
   *! Remove after global decommissioning
   */
  @Prop() readonly globalDecommissioning: boolean = false;

  @Prop() readonly optimizedLoading: boolean;
  @Prop() readonly optimizedLoadingLimit: number = 0;

  /**
   * Define collapse characterictics button type
   *! Default value: 'default' (qds default button)
   */
  @Prop() readonly collapseCharactericticsBtnType: string;

  /**
   * Overrides the default green color of the quartz component. Possible values: 'success', 'primary', 'secondary'.
   * Default value: 'success'
   */
  @Prop() readonly defaultQuartzColor: 'success' | 'primary' | 'secondary' = 'success';
  @State() resultsHasBeenFetched: boolean;

  @State() middlewareRegistry: PipesRegistry = {};

  /**
   * hide query in pagination hide=true or visible=false.
   */
  @Prop() readonly hideQueryInPagination?: boolean = false;

  /**
   * Returns a non-writable object with list of all registered middleares
   */

  /**
   *
   */
  @Prop() readonly filterPremiumIconUniqueId?: string;

  /**
   *
   */



  @Method()
  async getMiddlewareRegistry(): Promise<PipesRegistry> {
      return {...vendorMiddlewares, ...this.middlewareRegistry};
  }

  @Method()
  async registerMiddleware(name: string, call: PipeMiddlewareElement<any, any>): Promise<void> { //eslint-disable-line @typescript-eslint/no-explicit-any
      this.middlewareRegistry[name] = call;
      return;
  }

  @Method()
  async setFacetsAndCall(names: string[], nestedFilters?: Array<NestedFilter>): Promise<void> {
      const facets = await firstValueFrom(this.facetService.facets);
      const facetsUnique: FilterRequestUnique[] = FacetsService.setUnique(facets);
      const {currentQueries, currentChips} = FacetsService.prepareQueryByNames(names, facetsUnique, nestedFilters);
      const filterRequestQuery = currentQueries;
      this.facetService.addChipsToState(currentChips);

      if (filterRequestQuery.length > 0) {
        let additionalData: PipeDetailsAdditionalData = this.buildAdditionalPipeData(false);
        additionalData = additionalData ? additionalData : {};
        additionalData['forcedFilters'] = filterRequestQuery;
        FacetsService.callfacetChangedEventByRequestArray(this.facetChanged, filterRequestQuery);
        this.initPipeMiddleware({}, additionalData);
        this.scrollToTop();
      }

      this.facetsV2Service.currentContext = FilterChangeContext.mobile;
      this.facetsV2Service.updateStatesFromMobile();
      this.coseRequestService.initPipe(names)
        .pipe(
          takeUntil(this.unsubscriber$),
          first()
        )
        .subscribe(v => {
            this.coseRequestService.setOnQueryContext(QueryContext.mobileFilteringShowResults);
        });

  }

  @Method()
  async onLanguageTranslateChange(lang: string) {
      ExstResponseTranslator.translated = [];
      ExstResponseTranslator.language = lang;
  }

  @Method()
  async notifyAboutApiErrorResponse(error) {
      this.onApiError(error);
  }

  /**
   * When the query is updated, we look for search results for that query and render them in the tabs.
   */
  @Prop({attribute: 'query', mutable: true}) queryProp: string; //eslint-disable-line @stencil-community/strict-mutable
  private _currentPage = 1;
  @Prop({mutable: true}) perPage: number = ITEMS_PER_PAGE;
  @Prop() readonly showPerPage: boolean = true;
  @Prop() readonly notifyAboutServerError: boolean = false;
  @Prop({attribute: 'without-facet'}) readonly withoutFacet: boolean;
  @Prop() readonly mobileFilterButton: boolean;
  @Prop() readonly filtersItemsLimit: number;
  @Prop() readonly autoSwitchRedirectTab: number;
  @Prop() readonly tabNumber: number;
  @Prop() readonly isSubstitutionTool: boolean = false;


  @Prop({mutable: true})
    resultClickHandler?;

  @Prop({
    attribute: 'no-header-facets',
    mutable: true
  }) noHeaderFacets;
  /**
   * Function that can be injected from outside, and is capable of rendering results
   */
  @Prop() readonly resultRenderer?: ResultRenderer;
  @Prop() readonly gridView?: boolean = false;
  @State() isGridViewOn = this.gridView;

  @Prop({mutable: true}) gridViewSwitch?: boolean;
  @Prop() readonly defaultView?: string;

  private readonly gridViewSwitchVisible = this.gridViewSwitch;

  @Prop()
  readonly isAutocorrectEndpoint?: boolean = false;

  @Prop()
  readonly runAutocorrect?: boolean = false;

  @State() skipAutocorrection?: boolean = false;

  @Prop() readonly eShop?: boolean = false;

  private parseNoHeaderFacets(newValue) {
      if (typeof newValue == 'string' && newValue != '') {
        try {
          this.noHeaderFacets = JSON.parse(
            (newValue as string).split('\'').join('"')
          );
        } catch (exception) {
          this.noHeaderFacets = [];
          err(exception, 'Couldn\'t parse Fields as JSON', {
            this: this,
            newValue: newValue,
          });
        }
      }
  }

  @Prop({attribute: 'facets'}) readonly facetsProp: Array<string> | string;

  @State() facets: Array<string> = [];

  @Watch('facetsProp')
  parseFacets(newValue, oldValue = null, trigger = null) {
      debug('facetsProp processing', newValue, typeof newValue);
      if (
        typeof newValue == 'string' &&
      newValue != '' &&
      oldValue != newValue
      ) {
        try {
          this.facets = JSON.parse(
            (newValue as string).split('\'').join('"')
          );

          info('facets processed', this.facets);
        } catch (exception) {
          this.facets = [];
          err(exception, 'Couldn\'t parse Facets as JSON', {
            this: this,
            newValue: newValue,
            oldValue: oldValue,
            trigger: trigger,
          });
        }
      }
  }

  @Prop({attribute: 'business-units'}) readonly businessUnitsProp: Array<string> | string;

  @State() businessUnits: Array<string> = [];

  @Watch('businessUnitsProp')
  parseBusinessUnits(newValue, oldValue = null, trigger = null) {
      if (
        typeof newValue == 'string' &&
      newValue != '' &&
      oldValue != newValue
      ) {
        try {
          this.businessUnits = JSON.parse(
            (newValue as string).split('\'').join('"')
          );
        } catch (exception) {
          this.businessUnits = [];
          console.warn( //eslint-disable-line no-console
            exception,
            'Couldn\'t parse businessUnits as JSON',
            {
              this: this,
              newValue: newValue,
              oldValue: oldValue,
              trigger: trigger,
            });
        }
      }
  }

  /**
   * Filters to be applied for the tab. Cannot be changed by the user. Visibility not affected - can be changed by setting facet prop.
   */
  @Prop({attribute: 'enforce-filters'}) readonly enforceFiltersProp:
    | Array<Filter>
    | string;

  @Watch('enforceFiltersProp')
  parseEnforcedFilters(newValue, oldValue = null, trigger = null) {
      try {
        if (
          'string' == typeof newValue &&
        newValue != '' &&
        oldValue != newValue
        ) {
          this.enforceFilters = JSON.parse(
            (newValue as string).split('\'').join('"')
          );
        }
      } catch (exception) {
        this.enforceFilters = [];
        err(exception, 'Couldn\'t parse Fields as JSON', {
          this: this,
          newValue: newValue,
          oldValue: oldValue,
          trigger: trigger
        });
      }
  }

  /**
   * Initial filters to be pre-checked and applied. May be changed by the user
   */
  @Prop({attribute: 'initial-facet-state'})
  readonly initialFiltering: string;

  @State() enforceFilters: Array<Filter> = [];

  @State() accessibilityMode;

  @Listen('uiaas:accessibility', {target: 'window'})
  handleAccessibility(ev) {
      this.accessibilityMode = ev.detail;
  }

  @Watch('queryProp')
  queryUpdated(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.query = newValue;
        this.resultsHasBeenFetched = false;
        this.reset();
      }
  }


  @Watch('isVisible')
  isVisibleUpdated() {
      if (this.isVisible && this.optimizedLoading && !this.resultsHasBeenFetched) {
        this.reset();
        this.resultsHasBeenFetched = true;
      }
  }


  /**
   * execute this to execute the search query to the API
   */
  @Method()
  @Listen('search:reset')
  async reset(forceCall = false): Promise<void> {
      if (forceCall) {
        this.blockAutoAjaxCall = false;
        this.searchFacetsValuesToRemove = [
          ...this.searchFacetsValues.map(x => x.id)
        ];
        this.searchFacetsValues = [];
        if (this.searchableInputFilter) {
          this.searchableInputFilter.value = '';
        }
      }
      this.reflectedState = {
        tab: this.tabName,
        ajaxIsLoading: false,
        hasError: false,
        count: undefined //Initial value should remain undefined
      };

      if (this.facetService) {
        this.facetService.setFacetsRequestState([]);
        this.facetService.setFacetsState([]);
        this.facetService.setFacets(null);
        this.facetService.setFacetsChipsState([]);
      }

      if (this.facetsV2Service) {
        this.facetsV2Service.setFacetsRequestState([]);
        this.facetsV2Service.setFacetsState([]);
        this.facetsV2Service.setFacets(null);
        this.facetsV2Service.setFacetsChipsState([]);
      }

      this._currentPage = 1;
      this.aggregatedResults = [];
      this.facetStateCose = [];
      this.filtersChips = [];
      this.dateFacets = [];
      this.checkExistAndQueryCall(this.skipAutocorrection);
  }

  @Listen('search:productCatalogChanged')
  productCatalogChanged(event: CustomEvent<boolean>) {
      if (this.countryProductCatalog != event.detail) {
        this.countryProductCatalog = event.detail;
        if (this.facetService) {
          this.facetService.setFacetsRequestState([]);
          this.facetService.setFacetsState([]);
          this.facetService.setFacets(null);
          this.facetService.setFacetsChipsState([]);
        }

        this._currentPage = 1;
        this.aggregatedResults = [];
        this.facetStateCose = [];
        this.filtersChips = [];
        this.dateFacets = [];
        this.checkExistAndQueryCall(true);
      }
  }


  @Listen('search:autocorrectquery', {target: 'window'})
  searchAutocorrectQueryUpdate(event: CustomEvent): void {
      if (event.detail.changeQuery === true) {
        this.skipAutocorrection = true;
        this.checkExistAndQueryCall(true);
      }
  }

  /**
   * You can inject precooked data into the Tab component. Unstable API. Don't use in production.
   */
  @Prop({attribute: 'preload-data'}) readonly preloadDataAttribute: string;

  @Prop() readonly resultLoadingSpinner: boolean = true;

  @Prop() readonly disableFiltersOnLoading: boolean;

  @Prop() readonly accountId?: string;

  @Watch('preloadDataAttribute')
  preloadData(): void {
      try {
        const data = JSON.parse(this.preloadDataAttribute);
        this.reflectedState = {
          ...this.reflectedState,
          count: data?.count
        };
        this.query = data?.query;
        this.queryResult = data?.queryResult;
        this.facetStateCose = data?.facetStateCose;
      } catch (e) {
        err(
          'Preloading with fixed data - failed',
          e,
          this.preloadDataAttribute
        );
      }
  }

  @Prop() readonly openedFacets?: number;

  @Watch('isVisible')
  @Listen('search:completed')
  @Listen('search:error')
  sendSearchResultPresentationEvent(): void {
      if (this.isVisible) {
        const hits: number = this.reflectedState?.hasError
          ? -1
          : this.reflectedState?.count;

        // prevent double sending of GA events if autoSwitchRedirectTab is different
        // than default tab 0 - "All Results Tab"
        if (this.isVisible && this.autoSwitchRedirectTab && !hits) return;

        if (this.query !== undefined && hits !== undefined && !this.reflectedState?.ajaxIsLoading && this.appName != null) {
          this.SearchResultsPresentationEventEmitter.emit({
            event: 'search',
            appName: this.appName,
            kw55: this.queryResult?.term || '',
            suggestion: this.queryResult?.suggestion,
            guidedSearch: this.guidedUsed,
            sort: this.sortOption?.id,
            refinedSearch: false,
            siteSearchResults: hits,
            tabName: getSiteSearchCategory(this.tabName, this.appName),
          });
        }
      }
  }

  @Prop({mutable: true}) blockAutoAjaxCall: boolean = false; //eslint-disable-line @stencil-community/strict-mutable

  @Event({
    eventName: 'search:facetChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    facetChanged: EventEmitter<FacetChange>;

  /**
   * Calculation of tree structure of filter based on the flat filter state representation
   *
   * @param fs - facet state in a flat representation
   * @param topFacets - list of root facets
   *
   */


  @State() reflectedState: ReflectedTab;
  @Event({eventName: 'search:started'})
    searchStarted: EventEmitter<SearchStartedEvent>;
  @Event({eventName: 'search:completed'})
    searchCompleted: EventEmitter<SearchCompletedEvent>;
  @Event({eventName: 'search:cancelled'})
    searchCancelled: EventEmitter<SearchCancelledEvent>;
  @Event({eventName: 'search:error'})
    searchError: EventEmitter<SearchErrorEvent>;
  @Event({eventName: 'search:autocorrect', bubbles: true, composed: true})
    searchAutocorrect: EventEmitter<SearchAutocorrectEvent>;
  @Event({eventName: 'search:middlewaresCompleted'})
    middlewaresCompleted: EventEmitter<MiddlewaresCompleted>;

  @Watch('queryResult')
  updateCount(newValue /*, oldValue, trigger*/) {
      if (newValue?.searchResults?.totalHits) {
        this.reflectedState = {
          ...this.reflectedState,
          count: newValue?.searchResults?.totalHits
        };
      }
  }

  @Watch('apiEndpointProp')
  recalculateApiEndpoint(newValue: string | URL) {
      if (typeof newValue == 'string') {
        try {
          this.apiEndpoint = new URL(newValue as string, window.origin);
        } catch (e) {
          err(
            'Can\'t parse API endpoint as URL! Trying relevant path...',
            newValue
          );
        }
      } else {
        err('Wrong API format', newValue, typeof newValue);
      }
  }

  @State() facetStateCose: Array<Array<string>>;

  @State() filtersChips: Array<FacetChange> = [];
  @State() filtersChipsState: Array<FacetValue | FilterNode> = [];

  @Prop({mutable: true}) searchableInputFilter: SearchableInputFilter;
  @Prop({attribute: 'searchable-input-filter'}) readonly searchableInputFilterProp: string;

  @Watch('searchableInputFilterProp')
  parseSearchableInputFilter(value: string): void {
      try {
        if (value !== undefined) {
          this.searchableInputFilter = JSON.parse(value) as SearchableInputFilter;
        }
      } catch (exception) {
        err(exception, 'Couldn\'t parse searchable input filter', {
          this: this,
          val: value
        });
      }
  }

  @Listen('search:searchInputFilterChanged')
  searchInputFilterChangedListener(event: CustomEvent<SearchableFilterChanged>): void {
      const newSearchableInputFilter: SearchableInputFilter = {
        value: event.detail.value,
        fieldName: this.searchableInputFilter.fieldName,
        label: this.searchableInputFilter.label
      };
      this.searchableInputFilter = newSearchableInputFilter;
      if (event.detail.executeQuery) {
        this.aggregatedResults = [];
        this.checkExistAndQueryCall();
      }
  }

  @Listen('search:searchFacetButtonClicked')
  searchFacetButtonClickedListener(event: CustomEvent<any>): void {//eslint-disable-line @typescript-eslint/no-explicit-any
      this.aggregatedResults = [];
      event.preventDefault();
      event.stopPropagation();
      this.checkExistAndQueryCall();
  }

  @Listen('search:searchableFacetInputChanged')
  handleSearchFacetChanged(event: CustomEvent<SearchableFacetInputChanged>): void {
      const existingSearchFacetValue = this.searchFacetsValues.find(x => x.id === event.detail.uniqueId);
      if (existingSearchFacetValue) {
        existingSearchFacetValue.facetValue = event.detail.value;
      }
  }

  @Listen('search:searchableFacetInputEnterClicked')
  handleFacetInputEnterClicked(event: CustomEvent<any>): void {//eslint-disable-line @typescript-eslint/no-explicit-any
      this.aggregatedResults = [];
      event.preventDefault();
      event.stopPropagation();
      this.checkExistAndQueryCall();
  }

  @Listen('search:tableColumnSortChanged')
  tableColumnSortChanged(event: CustomEvent<SortableHeader>): void {
      this.searchTableSorting = event.detail;
      event.preventDefault();
      event.stopPropagation();
      this.checkExistAndQueryCall();
  }

  @Listen('search:facetChanged')
  facetChangedListener(event: CustomEvent<FacetChange>) {
      //TODO: this should be in reset()
      if (event.detail.blockQueryCall) {
        return;
      }

      this._currentPage = 1;
      if (!event.detail.searchFacet) {
        this.aggregatedResults = [];
      }
      if (event.detail.facetName === 'searchableInputFilter') {
        if (event.detail.action === 'remove') {
          this.searchableInputFilter.value = '';
          this.filtersChips = this.filtersChips.filter(x => x.id !== event.detail.id);
          this.checkExistAndQueryCall();
        }
        return;
      }

      if (event.detail.facetQuery === 'date') {
        if (event.detail.action === 'remove' || event.detail.value === '') {
          this.filtersChips = this.filtersChips.filter(x => x.facetType !== event.detail.facetType);
          this.updateDateFacetValue(event.detail.id, event.detail.dateFacetType, '');
        } else {
          this.updateDateFacetValue(event.detail.id, event.detail.facetType, event.detail.value);
          const dateTo = event.detail.facetType === FacetTypeEnum.DateEnd;
          const facetLabel = this.labelProvider(event.detail.id, event.detail.id);
          const label = `${facetLabel} - ${dateTo ? 'To' : 'From'}`;
          event.detail.dateFacetType = event.detail.facetType;
          event.detail.facetType = this.labelProvider('datefacet.dateFromLabel', label);
          this.addOrDeleteChip(event);
        }
        this.checkExistAndQueryCall();
        return;
      }

      const newFacetsCose = {...this.facetStateCose};
      let filterAction: FilterAction;

      if (event.detail.facetQuery === 'single') {
        for (const key in newFacetsCose) {
          if (key.includes(event.detail?.id)) {
            delete newFacetsCose[key];
          }
        }
      }

      if (event.detail.action === 'add') {
        newFacetsCose[event.detail?.id + '.' + event.detail?.value] =
        event.detail?.value;
        filterAction = 'Filter selected';
      } else {
        delete newFacetsCose[event.detail?.id + '.' + event.detail?.value];
        filterAction = 'Filter deselected';
      }

      if (!event.detail.searchFacet) {
        this.facetChangedEventEmitter.emit({
          event: 'filterClick',
          filterAction: filterAction,
          filterCategory: event.detail.facetTypeTranslation || event.detail.facetType,
          filterName: decodeURIComponent(event.detail.facetTranslation || event.detail.facetName),
          filterCount: event.detail.facetCount,
          tabName: this.tabName,
        });
      }

      this.facetStateCose = newFacetsCose;
      if (this.horizontalFilterPanel) {
        const facetId = event.detail.id ? event.detail.id : event.detail.facetName;
        if (filterAction === 'Filter deselected') {
          if (event.detail.facetQuery === 'search') {
            const index = this.filtersChips.findIndex(v => v.id === event.detail.id);
            this.filtersChips.splice(index, 1);
            this.searchFacetsValuesToRemove = [
              ...this.searchFacetsValuesToRemove,
              facetId
            ];
            this.searchFacetsValues = this.searchFacetsValues.filter(x => x.id !== facetId);
          } else {
            this.filtersChips = this.filtersChips.filter(chip => chip.facetName !== facetId
            && chip.facetValue !== event.detail.facetValue);
            this.searchFacetsValuesToRemove = [
              ...this.searchFacetsValuesToRemove,
              facetId
            ];
            this.searchFacetsValues = this.searchFacetsValues.filter(x => x.id !== facetId);
          }
        }
        if (filterAction === 'Filter selected') {
          if (!event.detail.searchFacet) {
            this.addOrDeleteChip(event);
          } else {
            const existingSearchFacetValue = this.searchFacetsValues.find(searchFacetsValue => searchFacetsValue.id === facetId);
            if (existingSearchFacetValue) {
              if (event.detail.facetValue === '') {
                this.searchFacetsValues = this.searchFacetsValues.filter(x => x.id !== facetId);
                this.searchFacetsValuesToRemove = [
                  ...this.searchFacetsValuesToRemove,
                  facetId
                ];
              } else {
                existingSearchFacetValue.facetValue = event.detail.facetValue;
              }
            } else {
              if (event.detail.facetValue !== '') {
                this.searchFacetsValues = [
                  ...this.searchFacetsValues,
                  event.detail
                ];
              } else {
                this.searchFacetsValuesToRemove = [
                  ...this.searchFacetsValuesToRemove,
                  facetId
                ];
              }
            }
          }
        }
      }

      if (!event.detail.searchFacet) {
        this.checkExistAndQueryCall();
      }
  }

  private updateDateFacetValue(facetId: string, facetType: string, value: string): void {
      this.dateFacets.filter(facet => {
          if (facet.id === facetId) {
            facet.children.filter(child => {
                if (child.type === facetType) {
                  child.value = value;
                }
            });
          }
      });
  }

  @State() dateFacets: DateFacet[] = [];

  private addOrDeleteChip(event: CustomEvent<FacetChange>): void {
      if (event.detail.facetQuery === 'search') {
        const existingFilterChip = this.filtersChips.find(chip => chip.id === event.detail.id);
        if (existingFilterChip) {
          if (event.detail.facetValue === '') {
            this.filtersChips = this.filtersChips.filter(chip => chip.id !== event.detail.id);
          } else {
            existingFilterChip.facetValue = event.detail.facetValue;
            existingFilterChip.value = event.detail.value;
          }
        } else {
          this.addFilterChip(event);
        }
      }

      if (event.detail.facetQuery === 'date') {
        const existingFilterChip = this.filtersChips.find(chip => chip.facetType === event.detail.facetType);
        if (existingFilterChip) {
          if (event.detail.facetValue === '') {
            this.filtersChips = this.filtersChips.filter(chip => chip.id !== event.detail.id);
          } else {
            existingFilterChip.facetValue = event.detail.facetValue;
            existingFilterChip.value = event.detail.value;
          }
        } else {
          this.addFilterChip(event);
        }
      } else {
        this.filtersChips = this.filtersChips.filter(chip => chip.id !== event.detail.id);
        this.addFilterChip(event);
      }
  }

  private addFilterChip(event: CustomEvent<FacetChange>): void {
      this.filtersChips.push({
        action: event.detail.action,
        facetCount: event.detail.facetCount,
        facetName: event.detail.facetName,
        facetQuery: event.detail.facetQuery,
        facetType: event.detail.facetType,
        facetValue: event.detail.facetValue,
        id: event.detail.id,
        searchFacet: event.detail.searchFacet,
        value: event.detail.value,
        dateFacetType: event.detail.dateFacetType
      });
  }

  private getFacetDisplayName(uniqueId: string): string {
      if (!this.facetsV2Service || !uniqueId) return '';

      let facetDisplayName = '';
      try {
        this.facetsV2Service.facetsOptions
          .pipe(takeUntil(this.unsubscriber$), first())
          .subscribe((fc) => {
              facetDisplayName = findParentValueInNestedArrayObject(fc.facets, 'displayName', 'uniqueId', uniqueId, 'children');
          });
      } catch (error) {
        err('Error fetching facet display name:', error);
      }
      return facetDisplayName;
  }

  @Prop({attribute: 'one-value-facets'}) readonly oneValueFacetsProp: Array<Filter> | string;

  @State() oneValueFacets: Array<Filter> = [];


  //Uwagi do sprawdzenia
  @Watch('oneValueFacetsProp')
  parseValueFacets(newValue, oldValue = null, trigger = null) {
      if (
        typeof newValue == 'string' &&
      newValue != '' &&
      oldValue != newValue
      ) {
        try {
          this.oneValueFacets = JSON.parse(newValue) as Array<Filter>;
          if (this.oneValueFacets.length > 0) {
            this.oneValueFacets.map((facet) => {
                const alreadyDeclaredFacet = this.facets.find(x => x == facet.name);
                if (alreadyDeclaredFacet !== undefined) {
                  if (facet.order) {
                    const index = this.facets.indexOf(facet.name);
                    const tmpFacet = this.facets[facet.order - 1];
                    this.facets[facet.order - 1] = facet.name;
                    this.facets[index] = tmpFacet;
                  }
                } else {
                  this.facets.push(facet.name);
                }
            });
          }
        } catch (exception) {
          this.oneValueFacets = [];
          err(
            exception,
            'Couldn\'t parse boolean-facets as JSON',
            {
              this: this,
              newValue: newValue,
              oldValue: oldValue,
              trigger: trigger
            });
        }
      }
  }

  @State() guidedUsed?: boolean = false;
  @Prop({mutable: true}) tabular?: boolean = false;//eslint-disable-line @stencil-community/strict-mutable
  @Prop({mutable: true}) tableConfiguration?: SearchTableConfiguration | string;//eslint-disable-line @stencil-community/strict-mutable
  @Prop() readonly horizontalFilterPanel: boolean = false;
  @Prop({mutable: true}) searchFacetsValuesToRemove: Array<string> = []; //eslint-disable-line @stencil-community/strict-mutable

  @State() countryProductCatalog?: boolean = true;
  @Prop() readonly productCatalog?: boolean = false;
  @Prop() readonly productCatalogWidget?: boolean = false;

  private dataflux = new DataFlux<SearchTabDataFlux>({
    eventbus: new EventBus()
  });
  @Prop() readonly parentDataFlux?: DataFlux<UiaasSearchDataFlux>;

  componentWillLoad() {
      this.facetService = FacetsService.getSetInstance(this.idProp);
      this.facets$ = this.facetService.facets.pipe(
        takeUntil(this.unsubscriber$),
      );
      if (this.showSimpleChips) {
        this.facetService.facetsChipsState.pipe(
          takeUntil(this.unsubscriber$),
          tap((state) => {
              this.filtersChipsState = state;
          })
        ).subscribe();
      }

      if (!this.resultClickHandler) {
        this.resultClickHandler = () => {
            return true;
        };
      }

      debug('Tab WillLoad', {
        tabTitle: this.tabTitle,
        query: this.query,
        queryProp: this.queryProp
      });

      if (!this.labelProvider) {
        info('Assigning default labelProvider to SearchTab component');

        this.labelProvider = (name, fallback) => {
            warn(
              'LabelProvider not provided in SearchTab component',
              [name, fallback]
            );
            return fallback;
        };
      }

      if (!this.tabName) {
      // convert to camelCase via https://stackoverflow.com/questions/2970525/converting-any-string-into-camel-case
        this.tabName = makeTabName(this.tabTitle);
      }

      this.reflectedState = {
        tab: this.tabName,
        ajaxIsLoading: false,
        hasError: false,
        count: undefined //Initial value should remain undefined
      };

      this.recalculateApiEndpoint(this.apiEndpointProp);
      this.parseEnforcedFilters(this.enforceFiltersProp);
      this.parseFacets(this.facetsProp);
      this.parseValueFacets(this.oneValueFacetsProp);
      this.parseSearchableInputFilter(this.searchableInputFilterProp);
      this.addFilterChips();
      this.addSearchFacetsValues();
      /* Initial state of the filters */
      this.parseBusinessUnits(this.businessUnitsProp);

      if (this.defaultView !== undefined) {
        if (this.defaultView.toLowerCase() == 'grid') {
          this.isGridViewOn = true;
        }

        if (this.defaultView.toLowerCase() == 'list') {
          this.isGridViewOn = false;
        }
      }

      this.strategies = StrategiesFactory.createStrategy(this.el);

      if (this.strategies.strategyData.exstTranslate && !ExstResponseTranslator.translateInterval) {
        ExstResponseTranslator.setConfig(this.strategies.strategyData.exstTranslate);
        ExstResponseTranslator.translateIntervalInit();
      }
      const baseUrlsData: BaseUrlsStrategyService = this.strategies.for(BASE_URLS);
      this.baseUrls = baseUrlsData.getBaseUrls();
  }

  private initV2FacestService() {
      if (!this.facetsV2Service) {
        this.facetsV2Service = FacetsV2Service.getSetInstance(this.idProp);
      }
      if (!this.coseRequestService) {
        this.coseRequestService = CoseRequestService.getSetInstance(this.facetsV2Service);
        this.coseRequestService.context = this;
      }

      if (this.facetsV2Service && !this.showSimpleChips) {
        this.facetsV2Service.facetsChipsState.pipe(
          takeUntil(this.unsubscriber$),
          tap((state) => {
              this.filtersChipsState = state;
          })
        ).subscribe();
      }
  }

  private addSearchFacetsValues() {
      if (this.horizontalFilterPanel && this.enforceFilters?.length > 0) {
        this.enforceFilters.map(filter => {
            this.searchFacetsValues = [
              ...this.searchFacetsValues,
              {
                facetName: filter.name,
                facetCount: 0,
                facetType: filter.name,
                facetValue: filter.value,
                facetQuery: 'search',
                action: 'remove',
                id: filter.name,
                searchFacet: true
              }
            ];
        });
      }
  }

  private addFilterChips() {
      if (this.horizontalFilterPanel) {
        this.enforceFilters.map(filter => {
            this.filtersChips.push({
              facetName: filter.name,
              facetCount: 0,
              facetType: filter.name,
              facetValue: filter.value,
              facetQuery: '',
              action: 'remove',
              id: filter.name,
              value: filter.value
            });
        });
      }
  }

  connectedCallback() {
      const htmlElement = document.querySelector('html');
      const isAccessibilityOn =
      htmlElement.classList.contains('se2-accessibility');
      this.accessibilityMode = isAccessibilityOn;
  }

  componentDidLoad() {
      debug('Tab DidLoad', {
        tabTitle: this.tabTitle,
        query: this.query,
        queryProp: this.queryProp
      });

      const facetsConfigDataParams: FacetsConfigStrategyDataParams | null = this.facetsConfigDataParams;

      if (facetsConfigDataParams && (facetsConfigDataParams.isNewFacetsLogic || facetsConfigDataParams.isMobileFiltering)) {
        this.initV2FacestService();
      }


      if (this.queryProp && this.apiEndpoint) {
        this.query = this.queryProp;
        this.checkExistAndQueryCall();
      }
      if (this.preloadDataAttribute) {
        this.preloadData();
      }
      this.SearchMountEmitter.emit(this.tabName);
  }

  @Event({
    eventName: 'pagination:changed',
    bubbles: true,
    composed: true,
    cancelable: true
  })
    paginationChange: EventEmitter<any>;//eslint-disable-line @typescript-eslint/no-explicit-any

  private paginationChanged(e: CustomEvent<any>) {//eslint-disable-line @typescript-eslint/no-explicit-any
      this._currentPage = e.detail.value;
      this.perPage = e.detail.perPage;
      this.checkExistAndQueryCall();
      this.paginationChange.emit({
        eventName: 'pagination:changed',
        currentPage: e.detail.value,
        perPage: e.detail.perPage
      });
  }

  @State() isMobile: boolean;

  @State() aggregatedResults: SearchApiDocument[] = [];
  @State() eolDiscontinuedResults: IEolDiscontinuedDetails = {
    documents: [],
    documentsGroup: [],
    replacementsTotalHits: 0
  };

  @State() searchTableSorting: SortableHeader;

  @State()
    mobileView: boolean = isMobile();

    @Listen('resize', {target: 'window'})
    @Listen('load', {target: 'window'})
  handleResize() {
      this.mobileView = isMobile();
      if(this.gridView) {
        this.isGridViewOn = !this.mobileView;
        if(this.gridViewSwitchVisible) {
          this.gridViewSwitch = !this.mobileView;
        }
      }
  }
    @Listen('uiaas:scrollToTop', {target: 'window'})
    onScrollToTopEvent() {
        this.scrollToTop();
    }

    private scrollToTop() {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }

  @Prop({mutable: true}) searchFacetsValues: Array<FacetChange> = []; //eslint-disable-line @stencil-community/strict-mutable


  componentShouldUpdate(newVal, oldVal, propName) {
      const notTriggerReload = ['reflectedState', 'isMobile'];
      return notTriggerReload.indexOf(propName) === -1;
  }

  private resultClickAnalytics(e: MouseEvent, index, url, commercialStatus) {
      e.preventDefault();
      this.searchResultClickEmitter.emit({
        event: 'searchResultClick',
        appName: this.appName,
        searchPagePosition: this._currentPage,
        searchResultPosition: index,
        site_search_category: getSiteSearchCategory(this.tabName, this.appName),
        siteSearchKeyword: this.query,
        is_filter_use: this.facetStateCose ? Object.keys(this.facetStateCose).length > 0 : false,
        rightButton: e.button !== 0,
        openUrl: url,
        clickedArea: SearchTab.getClickArea(e),
        tabName: getSiteSearchCategory(this.tabName, this.appName),
        clickType: this.getClickType(e),
        commStatus: commercialStatus,
        product: window.location.hostname,
        country: this.country,
        language: this.language,
        endpoint: this.apiEndpoint,
        apigeeTokenUrl: this.apigeeTokenUrl,
        apigeeAuth: this.apigeeAuth,
        searcher: this.queryResult.searcher,
      });

      return this.resultClickHandler(e);
  }

  @Listen('search:sortingSelected')
  handleSortingOptionSelected(e) {
      this.sortOption = {
        id: e.detail.id,
        order: 'descending'
      };
      this._currentPage = 1;
      this.aggregatedResults = [];
      this.checkExistAndQueryCall();

  }

  @Listen('search:ResultCardClick')
  handleResultCardClick(e) {
      e.stopPropagation();
      const {
        searchResultPosition,
        rightButton,
        openUrl,
        title,
        id,
        category,
        resutCardType,
        clickedArea,
        clickType,
        commStatus,
        product,
        circularNature,
        price,
        totalPrice,
        currency,
        quantity,
        search_location,
        isPromotedWidget
      } = e.detail;

      this.searchResultClickEmitter.emit({
        event: 'searchResultClick',
        appName: this.appName,
        searchPagePosition: this._currentPage,
        searchResultPosition,
        site_search_category: getSiteSearchCategory(this.tabName, this.appName),
        siteSearchKeyword: this.query,
        is_filter_use: this.facetStateCose ? Object.keys(this.facetStateCose).length > 0 : false,
        rightButton,
        openUrl,
        title,
        id,
        category,
        resutCardType,
        clickedArea,
        tabName: getSiteSearchCategory(this.tabName, this.appName),
        clickType,
        commStatus,
        product,
        country: this.country,
        language: this.language,
        endpoint: this.apiEndpoint,
        apigeeTokenUrl: this.apigeeTokenUrl,
        apigeeAuth: this.apigeeAuth,
        searcher: this.queryResult.searcher,
        circularNature,
        price,
        totalPrice,
        currency,
        quantity,
        search_location,
        isPromotedWidget
      });
  };

  private sortingElement = (
    scrollToTop: boolean,
  ) => {
      return (
        <ResultsSorting
          sortingOptions={this.queryResult.sortOptions}
          defaultValueId={this.sortOption?.id || sortingOptions.RELEVANCY}
          enableSorting={this.sortingResults}
          labelProvider={(e, f) => this.labelProvider(e, f)}
          scrollToTop={scrollToTop}
        />
      );
  };

  private resultList(isHideLabels: boolean) {
      if (this.queryResult == undefined) return <div class="result-list empty"/>;
      const isHideQueryInPagination = this.hideQueryInPagination || isHideLabels;
      const resultIndexOffset = this.eolDiscontinuedResults?.documents?.length || 0;

      return (
        <div>
          <TabularView
            tabularView={this.tabular}
            query={this.query}
            labelProvider={(e, f) => this.labelProvider(e, f)}
            aggregatedResults={this.aggregatedResults}
            tableConfiguration={this.tableConfiguration}
            reflectedState={this.reflectedState}
            perPage={this.perPage}
            currentPage={this._currentPage}
            queryResult={this.queryResult}
            paginationChanged={this.paginationChanged}
            disableFiltersOnLoading={this.disableFiltersOnLoading}
            isDashboardView={this.isDashboardView}
            context={this}
          />
          <DefaultView
            mobileView={this.mobileView} // Remove after finishing mobile view for dashboard
            tabularView={this.tabular}
            query={this.query}
            labelProvider={(e, f) => this.labelProvider(e, f)}
            aggregatedResults={this.aggregatedResults}
            reflectedState={this.reflectedState}
            perPage={this.perPage}
            currentPage={this._currentPage}
            queryResult={this.queryResult}
            paginationChanged={this.paginationChanged}
            disableFiltersOnLoading={this.disableFiltersOnLoading}
            strategies={this.strategies}
            resultRenderer={this.resultRenderer}
            resultClickAnalytics={this.resultClickAnalytics}
            country={this.country}
            language={this.language}
            gridView={this.isGridViewOn}
            isEcommerceMode={this.isEcommerceMode}
            eShop={this.eShop}
            rtl={this.rtl}
            context={this}
            sortOption={this.sortOption}
            sortingResults={this.sortingResults}
            scrollToTop={true}
            isDashboardView={this.isDashboardView}
            productCatalogWidget={this.productCatalogWidget}
            hideQueryInPagination={isHideQueryInPagination}
            isAdditionalConditionsWidgetActive={this.isAdditionalConditionsWidgetActive}
            isMiaPromotionWidget={this.isMiaPromotionWidget}
            miaData={this.parentDataFlux?.root?.miaData}
            marketingProductBadgesFilters={this.parentDataFlux?.root?.marketingProductBadgesFilters}
            wcEventData={this.parentDataFlux?.root?.wcEventData}
            isUnifiedCheckout={this.isUnifiedCheckout}
            isSubstitutionTool={this.isSubstitutionTool}
            resultIndexOffset={resultIndexOffset}
          />
        </div>
      );
  }

  /**
   * Should we show facets?
   * @return boolean
   */
  private shouldShowFacetBar() {
      if (this.horizontalFilterPanel || this.tabular) {
      // show vertical filters
        return true;
      } else {
        return (
          this.queryResult?.facets &&
        this.facets != null &&
        this.queryResult?.facets?.length > 0
        );
      }
  }

  private handleOneValueFacet(facet: Facet) {
      const oneValueFacets = this.oneValueFacets.find(
        (x) => x.name === facet.fieldName
      );
      if (oneValueFacets) {
        const oneValueFacetValues = facet.fieldValues.find(
          (x) => x.fieldValue === oneValueFacets.value
        );
        if (oneValueFacetValues) {
          const oneValueFacet = facet.fieldValues.find(
            (x) => x.fieldValue === oneValueFacets.value
          );
          if (oneValueFacet !== undefined) {
            facet.fieldValues = [{fieldValue: oneValueFacet.fieldValue, count: oneValueFacet.count}];
          }
        }
      }

      return facet;
  }

  private filterPane() {
      if (this?.queryResult?.facets.length > 0) {
        let limitedFacets = this.queryResult.facets;

        if (!this.horizontalFilterPanel) {
          limitedFacets = this.queryResult.facets.map((e) =>
            this.limitFacet(e as Facet)
          );
          if (this.oneValueFacets && this.oneValueFacets.length > 0) {
            limitedFacets = limitedFacets.map((e) =>
              this.handleOneValueFacet(e as Facet)
            );
          }
        }

        const filtersBody = <Filters
          selectedChildren={this.selectedChildren}
          queryResult={this.queryResult}
          horizontalFilterPanel={this.horizontalFilterPanel}
          idProp={this.idProp}
          mobileFilterButton={this.mobileFilterButton}
          mobileView={this.mobileView}
          labelProvider={(e, f) => this.labelProvider(e, f)}
          limitedFacets={limitedFacets}
          facetStateCose={this.facetStateCose}
          facetChanged={this.facetChanged}
          tabName={this.tabName}
          openedFacets={this.openedFacets}
          noHeaderFacets={this.noHeaderFacets}
          filtersItemsLimit={this.filtersItemsLimit}
          reflectedState={this.reflectedState}
          disableFiltersOnLoading={this.disableFiltersOnLoading}
          rtl={this.rtl}
          query={this.query}
          searchFacetsValuesToRemove={this.searchFacetsValuesToRemove}
          enforcedFilters={this.enforceFilters}
          searchableInputFilter={this.searchableInputFilter}
          filtersChips={this.filtersChips}
          scrollToTop={this.scrollFacetsToTop}
          productCatalog={this.productCatalog}
          hiddenFacets={this.hiddenFacets}
          defaultQuartzColor={this.defaultQuartzColor}
          newFacetsLogicConfig={this.facetsConfigDataParams}
          coseRequestService={this.coseRequestService}
          facetsService={this.facetsV2Service}
          filterPremiumIconUniqueId={this.filterPremiumIconUniqueId}
          collapseCharactericticsBtnType={this.collapseCharactericticsBtnType}
          headerAdditionalComponents={{
            element: (scrollToTop) => this.sortingElement(scrollToTop),
            elementHeight: this.sortingResults ? 42 : 0
          }}
        />;

        return (<FiltersConditionalWraper
          child={filtersBody}
          labelProvider={(e, f) => this.labelProvider(e, f)}
          mobileFilterButton={this.mobileFilterButton}
          mobileView={this.isMobile}
        />);
      }
  }

  /**
   * Limit top level facets values accordingly to the enforce-filter prop.
   * Note: child facets even if defined won't be affected, enforce-filters allow only to specify flat list of filters
   *
   * @param f: Facet - facet to be limited
   *
   */
  private limitFacet(f: Facet) {
      if (this.enforceFilters && this.enforceFilters.length > 0) {
        const ef = this.enforceFilters.filter((v) => v.name == f.fieldName);
        if (ef.length > 0) {
          return {
            fieldName: f.fieldName,
            // facet values limited to the values occurring in enforce-filters for the same facet
            fieldValues: f.fieldValues.filter((fv) =>
              ef.some((v) => v.value == fv.fieldValue)
            )
          };
        } else {
        // no enforce filters defined for the facet -> return a facet
          return f;
        }
      } else return f;
  }

  /**
   * Calculates children facets to be displayed under top level facet.
   * Only selected facets are taken into consideration.
   *
   * @param f: Facet - facet for which child facets are to be calculated
   *
   */
  private selectedChildren(f: Facet): Array<Facet> {
      let selectedValues: Array<string> = [];
      const facetHasState = (this.facetStateCose && Object.keys(this.facetStateCose)?.length > 0);
      if (facetHasState) {
        selectedValues = Object.values(
          this.facetStateCose
        ) as unknown as Array<string> || [];
        return f.fieldValues
          ? f.fieldValues
            .filter((fv) => fv.children)
            .filter((v) => selectedValues.includes(v.fieldValue))
            .map((v) => {
                return {
                  displayName: v.displayName,
                  fieldName: v.fieldValue,
                  fieldValues: v.children
                };
            })
          : [];
      } else {
        return [];
      }
  }

  private beforePipeMiddlewareActions(): void {
      this.reflectedState = cloneObjectWithNoReference({...this.reflectedState, ajaxIsLoading: true});
      this.searchStarted.emit(cloneObjectWithNoReference({...this.reflectedState, term: this.query}));
  }


  render() {
      this.isVisible = this.parentDataFlux?.root.activeTab === this.tabNumber;
      const hideLabelsStrategy: HideLabelsStrategyService = this.strategies.for(HIDE_LABELS);
      const isHideLabels = hideLabelsStrategy?.isHideLabels(this.query);
      this.facetsConfigDataParams = this.getFacetsConfigStrategyData();
      const isNewFacetsLogic = this.facetsConfigDataParams && this.facetsConfigDataParams?.isNewFacetsLogic && this.facetsConfigDataParams?.newLogicConfig && !this.mobileView;
      const areFiltersVisible = this?.queryResult?.facets.length > 0;
      return (
        <div class={{'search-tab--rtl': this.rtl}} dir={this.rtl ? 'rtl' : 'auto'}>
          {
            (this.queryResult && this.queryResult?.stats?.totalHits && this.mobileView && this.facetsConfigDataParams && this.facetsConfigDataParams?.isMobileFiltering)
              ? <uiaas-search-mobile-filters-count-results
                labelProvider={(e, f) => this.labelProvider(e, f)}
                totalHits={this.queryResult?.stats?.totalHits as number}
              />
              : ''
          }
          <TabTitle
            showTitle={this.showTitle}
            tabTitle={this.tabTitle}
            showDocumentCount={this.showDocumentCount}
            count={this.reflectedState.count}/>
          {this.queryResult && <slot name="under-title"/>}
          {this.notifyAboutServerError && (
            <uiaas-search-server-error
              query={this.query}
              labelProvider={(e, f) => this.labelProvider(e, f)}
              reflectedState={this.reflectedState}
            />
          )}
          {this.queryResult &&
          ((this.queryResult.searchResults.totalHits == 0 && !this.tabular) ? (

            !this.reflectedState.ajaxIsLoading && (<slot name="no-results">
              <NoResults
                loading={this.reflectedState.ajaxIsLoading}
                labelProvider={(e, f) => this.labelProvider(e, f)}
                query={this.query}
                globalDecommissioning={this.globalDecommissioning}
                language={this.language}
                country={this.country}
                isProductNaCountrySelector={this.isProductNaCountrySelector}
                productNaCountrySelectorLocales={this.parentDataFlux?.root?.productNaCountrySelectorLocales}
                totalHits={this.reflectedTabs?.[Tab.ALL]?.count}
                tabName={this.tabName}
                brand={this.strategies.strategyData?.cardType?.params?.brand}
              />
              <slot name="after-no-results"/>
            </slot>)

          ) : (
            <div class={'results__list-of-results'
              + (this.horizontalFilterPanel ? '-horizontal' : '')
              + (this.productCatalog ? '-product-catalog' : '')
              + (isNewFacetsLogic ? ' padding-top-none' : '')
            }>
              <FacetsPane
                shouldShowFacetBar={this.shouldShowFacetBar()}
                isDashboardView={this.isDashboardView}
                horizontalFilterPanel={this.horizontalFilterPanel}
                filterPane={() => this.filterPane()}/>
              <div class={'results__section' + (this.showSimpleChips ? ' has-simple-chips' : '')}>
                {isNewFacetsLogic && this.facetsV2Service && this.shouldShowFacetBar()
                  ? <uiaas-search-results-block-top
                    queryResult={this.queryResult}
                    sortOption={this.sortOption}
                    sortingResults={this.sortingResults}
                    showChildren={this.facetsConfigDataParams.newLogicConfig.showChildren}
                    facetsV2Service={this.facetsV2Service}
                    showChips={!!this.facetsConfigDataParams.newLogicConfig.showChips}
                    dividerFacets={this.facetsConfigDataParams.newLogicConfig.dividerFacets}
                    filtersChipsState={this.filtersChipsState}
                    hiddenFacets={this.hiddenFacets}
                    tabName={this.tabName}
                    gridViewSwitch={this.gridViewSwitch}
                    switchGridView={(e) => this.switchGridView(e)}
                    gridView={this.isGridViewOn}
                    accessibilityMode={this.accessibilityMode}
                    rtl={this.rtl}
                    aggregatedResults={this.aggregatedResults}
                    clearHandler={(filterChip) => {
                        const facetCategoryDisplayName = this.getFacetDisplayName(filterChip.uniqueId);
                        this.facetsV2Service.removeFilterFromDefaultStoreByUniqueId(filterChip.uniqueId);
                        if (this.facetsV2Service) {
                          this.facetsV2Service.setChipRemoved(filterChip);
                        }
                        this.chipRemoveOnTab.emit({...filterChip, 'tabName': this.tabName, facetCategoryDisplayName});
                    }}
                    labelProvider={(e, f) => {
                        return this.labelProvider(e, f);
                    }}
                  />
                  : ''}
                {(this.tabular || isNewFacetsLogic) && this.shouldShowFacetBar() ? '' :
                  <div class={{'results__container': !this.isDashboardView}}>
                    <div class={'results__menu' + (this.productCatalog ? '-product-catalog' : '')}>
                      <slot name="results-actions"/>
                      <ProductCatalogChip
                        visible={this.productCatalog}
                        countryProductCatalog={this.countryProductCatalog}
                        labelProvider={(e, f) => this.labelProvider(e, f)}
                      />
                      <NumberOfResults
                        presentedResults={this.aggregatedResults.length}
                        totalResults={this.reflectedState.count}
                        labelProvider={(e, f) => this.labelProvider(e, f)}
                        term={this.query}
                        resultSummary={this.resultSummary}
                        isLoading={this.reflectedState.ajaxIsLoading}
                        hideLabelWithTerm={isHideLabels}
                      />
                      <OnlyYourCountryApc
                        appName={this.appName}
                        isCountryOnly={this.isCountryOnly}
                        handleChange={(e) => this.handleChange(e.target.checked)}
                        labelProvider={(e, f) => this.labelProvider(e, f)}
                        country={this.country}
                        language={this.language}/>
                      <GridSwitchButton
                        gridViewSwitch={this.gridViewSwitch}
                        switchGridView={(e) => this.switchGridView(e)}
                        gridView={this.isGridViewOn}
                        accessibilityMode={this.accessibilityMode}
                        labelProvider={(e, f) => {
                            return this.labelProvider(e, f);
                        }}
                      />
                      {!areFiltersVisible ? this.sortingElement(false) : ''}
                    </div>
                  </div>
                }
                {this.showSimpleChips ? <svs-chips-view
                  filtersChips={this.filtersChipsState}
                  clearHandler={(filterChip) => {
                      this.facetService.clearFacet(filterChip);
                      if (this.facetsV2Service) {
                        this.facetsV2Service.setChipRemoved(filterChip);
                      }
                  }}
                  hiddenFacets={this.hiddenFacets}
                >
                </svs-chips-view> : ''}

                <slot name="before-results"></slot>
                <slot name="second-banner-before-results"></slot>

                <div class="eol-result-list">
                  <EolDiscontinuedResultList
                    loading={this.reflectedState.ajaxIsLoading}
                    tabular={this.tabular}
                    mobileView={this.mobileView}
                    isSubstitutionTool={this.isSubstitutionTool}
                    eolDiscontinuedResults={this.eolDiscontinuedResults}
                    labelProvider={(e, f) => this.labelProvider(e, f)}
                    tabName={this.tabName}
                    strategies={this.strategies}
                    rtl={this.rtl}
                    term={this.queryResult.term}
                    language={this.language}
                    country={this.country}
                  />
                </div>
                <ResultList
                  loading={this.reflectedState.ajaxIsLoading}
                  tabular={this.tabular}
                  resultLoadingSpinner={this.resultLoadingSpinner}
                  mobileView={this.mobileView}
                  resultList={() => this.resultList(isHideLabels)}/>
              </div>
              {this.strategies.strategyData &&
              this.strategies.strategyData?.cardType &&
              this.strategies.strategyData.cardType?.params &&
              this.strategies.strategyData.cardType.params?.isSidePanelFeature &&
              this.strategies.strategyData.cardType.params?.productSelector ?
                <uiaas-search-product-side-panel-event-wrapper
                  tabName={getSiteSearchCategory(this.tabName, this.appName)}
                  appName={this.appName}
                  country={this.country}
                  language={this.language}
                  currentPage={this.resultsCurrentPage}
                  term={this.queryResult.term}
                  isFilterInUse={this.facetStateCose ? Object.keys(this.facetStateCose).length > 0 : false}
                  productSelector={this.strategies.strategyData.cardType.params.productSelector}
                  myProductsUrl={this.baseUrls.params?.myProductsUrl}
                  myProductsDataSheetTemplate={this.baseUrls.params?.myProductsDataSheetTemplate}
                  labelProvider={(e, f) => this.labelProvider(e, f)}
                >
                  <uiaas-search-product-side-panel
                    gridView={this.isGridViewOn}
                    country={this.country}
                    language={this.language}
                    brand={this.strategies.strategyData.cardType.params.brand}
                    searcher={this.tabName}
                    queryResult={this.queryResult}
                    documents={this.queryResult.documents}
                    term={this.queryResult.term}
                    rtl={this.rtl ? true : false}
                    labelProvider={(e, f) => {
                        return this.labelProvider(e, f);
                    }}
                    productSidePanelService={new ProductSidePanelService(this.apigeeTokenUrl, this.apigeeAuth, this.apiKey, this.apiEndpoint)}
                    isSubstitutionTool={this.isSubstitutionTool}
                    apigeeAuth={this.apigeeAuth}
                    apigeeTokenUrl={this.apigeeTokenUrl}
                    apiEndpoint={this.apiEndpointProp}
                  ></uiaas-search-product-side-panel>
                </uiaas-search-product-side-panel-event-wrapper>
                : ''}
            </div>
          ))}
        </div>
      );
  }

  private checkExistAndQueryCall(skipAutocorrection?): void {
      if (this.blockAutoAjaxCall) {
        return;
      }
      if (!skipAutocorrection) {
        skipAutocorrection = false;
      }

      if (this.apiEndpoint) {
        const additionalData: PipeDetailsAdditionalData =
        this.buildAdditionalPipeData(skipAutocorrection);

        this.initPipeMiddleware({}, additionalData);
        return;
      }
  }

  @Method()
  async checkExistAndQueryCallMethod(skipAutocorrection?): Promise<void> {
      return this.checkExistAndQueryCall(skipAutocorrection);
  }

  // eslint-disable-next-line
  public getSortingOption() {
      // Required to acces sorting option without asynchronous method
      return this.sortOption;
  }

  private buildAdditionalPipeData(
    skipAutocorrection: boolean | undefined
  ): PipeDetailsAdditionalData | undefined {
      let additionalData: PipeDetailsAdditionalData | undefined;
      if (skipAutocorrection) {
        additionalData = {skipAutocorrection};
      }

      if (this.sortingResults && this.sortOption?.id) {
        additionalData = {...additionalData, sort: this.sortOption};
      }
      return additionalData;
  }

  private initPipeMiddleware(
    adapterConfig: MiddlewarePipeAdapterConfig,
    additionalData?: PipeDetailsAdditionalData
  ): void {
      this.beforePipeMiddlewareActions();
      if (this.facetsV2Service) {
        this.facetsV2Service.setCheckboxesInActiveState(true);
      }
      middlewareSearchTabAdapater<SearchApi>(
        this,
        adapterConfig,
        additionalData
      )
        .pipe(
          takeUntil(this.unsubscriber$),
          take(1),
          tap(() => {
              if (this.facetsV2Service) {
                this.facetsV2Service.setCheckboxesInActiveState(false);
              }
          })
        )
        .subscribe({
          error: (error) => {
              this.onApiError(error);
          }
        });
  }

  //@ts-ignore
  private onApiError(error: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
      this.reflectedState = {
        ...this.reflectedState,
        ajaxIsLoading: false,
        hasError: true,
      };
      this.searchError.emit({
        ...this.reflectedState,
        errorStatus: error.status,
        errorCode: error.error,
        errorMessage: error.message,
        ajaxIsLoading: false,
      });

      err('returned error on ajax query!', error, this);
      this.queryResult = null;
  }

  private switchGridView(view: string): void {
      this.viewChangeEmitter.emit({
        event: 'viewType',
        viewType: view,
        initialViewType: this.isGridViewOn ? viewType.GRID : viewType.LIST,
        tabName: this.tabName
      });
      if (view === viewType.LIST && this.isGridViewOn) {
        this.isGridViewOn = false;
      } else if (view === viewType.GRID && !this.isGridViewOn) {
        this.isGridViewOn = true;
      }
  }


  private getClickType(e): string {
      if (e.key === 'Enter') {
        return e.key;
      }
      if (e.button === 0) {
        return 'left';
      } else {
        return 'right';
      }
  }

  private static getClickArea(e): string {
      const eventClassList = e.currentTarget.classList;

      if (eventClassList.contains('result-title__link')) {
        return 'title';
      } else if (eventClassList.contains('result__image-placeholder')) {
        return 'image';
      } else if (eventClassList.contains('result__range_link')) {
        return 'range';
      } else if (eventClassList.contains('document-link__link')) {
        return 'attachment';
      } else {
        return 'background';
      }
  }
}

/*
  https://schneider.atlassian.net/wiki/spaces/WMS/pages/2080604357/Search+UI+Component+-+scope+and+high+level+design+DRAFT
  *
  UI ref: https://exchange.se.com/search-results?problemToSolve=Asset%20Performance&problemToSolve=Energy%20Efficiency&segment=Healthcare&segment=Metals&segment=Oil%20%26%20Gas&source=marketplace
  *
  * https://bitbucket.org/schneideripo/se-faq-pilot/src/develop/
  *
  * */
